import { useEffect, useState } from "react";
import { api } from "../../api";

const COLS = ["hcl", "h2so4", "naoh", "na2co3", "hno3", "ch2o"];
const LABELS = { hcl: "HCl", h2so4: "H₂SO₄ (98%)", naoh: "NaOH (Lye)", na2co3: "Na₂CO₃", hno3: "HNO₃ (Weak)", ch2o: "CH₂O (Formalin)" };
const TAG_LABEL = { ok: "Compatible", no: "Avoid", caution: "Caution" };
const VERDICT_STYLES = {
  ok: { color: "#1f6a4f", bg: "#e2f0ea", label: "Compatible" },
  caution: { color: "#8a5a13", bg: "#fdf1de", label: "Use with caution" },
  no: { color: "#a8341f", bg: "#fbeeeb", label: "Avoid" },
};

export default function Compat({ onRequestQuote }) {
  const [rows, setRows] = useState([]);
  const [mat, setMat] = useState("HDPE");
  const [chem, setChem] = useState("hcl");
  const [temp, setTemp] = useState("40");
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.getCompatibility().then((r) => {
      setRows(r);
      if (r.length && !r.find((x) => x.mat === mat)) setMat(r[0].mat);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = parseFloat(temp);
    api.checkCompatibility(mat, chem, isNaN(t) ? undefined : t).then(setResult).catch(() => setResult(null));
  }, [mat, chem, temp]);

  const style = result ? VERDICT_STYLES[result.verdict] : null;

  return (
    <section className="panel" id="compat" data-testid="compat">
      <div className="wrap" data-testid="section-compat-inner">
        <div className="panel-head">
          <div className="eyebrow">Tool · What-if checker</div>
          <h2>Tank &amp; piping compatibility</h2>
          <p>Full matrix below, plus a &ldquo;what-if&rdquo; checker: pick material + chemical + operating
             temperature and get an instant verdict with reasoning &amp; suggested alternates.</p>
        </div>

        <div className="whatif-card" data-testid="whatif-card">
          <div className="whatif-inputs">
            <div>
              <label className="mono">Material</label>
              <select value={mat} onChange={(e) => setMat(e.target.value)} data-testid="whatif-material">
                {rows.map((r) => <option key={r.mat} value={r.mat}>{r.mat}</option>)}
              </select>
            </div>
            <div>
              <label className="mono">Chemical</label>
              <select value={chem} onChange={(e) => setChem(e.target.value)} data-testid="whatif-chem">
                {COLS.map((c) => <option key={c} value={c}>{LABELS[c]}</option>)}
              </select>
            </div>
            <div>
              <label className="mono">Operating temperature (°C)</label>
              <input type="number" min="0" max="200" value={temp} onChange={(e) => setTemp(e.target.value)} data-testid="whatif-temp" />
            </div>
          </div>

          {result && (
            <div className="whatif-result" data-testid="whatif-result">
              <div className="whatif-verdict" style={{ background: style.bg, color: style.color }} data-testid="whatif-verdict">
                {style.label}
                {result.temp_max_c ? <span className="whatif-limit mono">Max {result.temp_max_c}°C</span> : null}
              </div>
              <ul className="whatif-reasons">
                {result.reasons?.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
              {result.alternates?.length > 0 && (
                <div className="whatif-alt">
                  <span className="mono">Suggested alternates:</span>
                  {result.alternates.map((a, i) => (
                    <button key={i} className="whatif-alt-chip" onClick={() => setMat(a)} data-testid={`whatif-alt-${i}`}>
                      {a}
                    </button>
                  ))}
                </div>
              )}
              <div className="whatif-actions">
                <button className="print-btn" onClick={() => window.print()} data-testid="whatif-print">Print spec sheet</button>
                <button className="btn btn-brand btn-sm" onClick={() => onRequestQuote?.("what-if")} data-testid="whatif-request-quote">
                  Ask sales about this combination
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="sub-panel-head">
          <div className="eyebrow">Reference</div>
          <h3>Full compatibility matrix</h3>
        </div>

        <div style={{ overflowX: "auto" }}>
          <table className="compat" data-testid="compat-table">
            <thead>
              <tr>
                <th>Material</th>
                {COLS.map((c) => <th key={c}>{LABELS[c]}</th>)}
                <th>Max °C</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td>{row.mat}</td>
                  {COLS.map((c) => (
                    <td key={c}><span className={`tag ${row[c]}`}>{TAG_LABEL[row[c]]}</span></td>
                  ))}
                  <td className="mono">{row.temp_max_c ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}
